import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Loan, CreateLoan, UpdateLoan } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['loans', companyId] as const;

export function useLoans(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () => api<Loan[]>(`/companies/${companyId}/loans?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useLoan(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['loans', companyId, id],
    queryFn: () => api<Loan>(`/companies/${companyId}/loans/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateLoan(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateLoan, 'companyId'>) =>
      apiJson<typeof input, Loan>('POST', `/companies/${companyId}/loans`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateLoan(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLoan }) =>
      apiJson<UpdateLoan, Loan>('PATCH', `/companies/${companyId}/loans/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteLoan(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, Loan>('DELETE', `/companies/${companyId}/loans/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
